import React, {useMemo, useState} from 'react';
import {
    CardFooter,
    Button,
    Body1,
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogBody,
    DialogContent,
    DialogActions,
    Input,
    InputOnChangeData,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    tokens,
    List,
    ListItem
} from "@fluentui/react-components";
import { ApplicationMode, IActivationRequest, ICustomer, ILicense } from '../IDB';
import { Server } from "../apis";
import { useNavigate } from 'react-router';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { licenseMap } from '../pages/LicenseBuilder';

interface ICustomerCardFooter {
    activeLicense: ILicense | undefined;
    closeToExpiration: boolean;
    customer: ICustomer;
    setLicenseFileElement: (ele: JSX.Element | undefined) => void
}

const useStyles = makeStyles({
    grid: {
        display: 'grid',
        gridTemplateAreas: "'left right'",
        gridTemplateColumns: '4fr 1fr',
        gap: tokens.spacingHorizontalM
    },
    left: {
        gridArea: 'left',
        display: 'flex',
        justifyContent: 'start',
        gap: tokens.spacingHorizontalM
    },
    right: {
        gridArea: 'right',
        display: 'flex',
        justifyContent: 'end',
    },
    hostIdInput: {
        width: '100%'
    }
});

const CustomerCardFooder = (props: ICustomerCardFooter) => {

    const server = useMemo(() => Server.getInstance(), []);
    const navigate = useNavigate();
    const styles = useStyles();
    const [hostid, setHostid] = useState<string>("");
    const [revokeDialogOpen, setRevokeDialogOpen] = useState<boolean>(false);
    const [transferState, setTransferState] = useState<0 | 1 | 2>(0); // 0 dialog not open, 1 dialog open, 2 renew success
    const [historyDialogOpen, showHistoryDialog] = useState<boolean>(false);

    const setActivationError = (msg: string) => {
        window.alert(msg);
    };

    const resetState = () => {
        setHostid("");
        setRevokeDialogOpen(false);
        showHistoryDialog(false);
    }

    const activateProduct = async () => {
        const data: IActivationRequest = {
            activationCode: props.activeLicense!.code,
            diskSerialNumber: null,
            hostName: hostid || "portal",
            macAddress: null,
            internalIp: "localhost",
            vmType: null,
            dongle: null,
            applicationMode: ApplicationMode.Unknown
        };
        try {
            const licenseFile = await server.activateProduct(data);
            const fileContent = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), licenseFile], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(fileContent);
            const anchorElement = <Button as="a" href={url} download="license.lic" data-text={licenseFile}>Download</Button>;
            props.setLicenseFileElement(anchorElement);
        } catch (error) {
            if (error) {
                if (error.status === 400) {
                    if (error.response.data.detail.indexOf("CodeIsNotValid") > -1) {
                        setActivationError("Code is not valid. Check the code and try again.");
                    } else if (error.response.data.detail === "ComputerIdIsInvalid") {
                        setActivationError("No hostname was provided, or the provided DiskSerialNumber and MacAddress are invalid.");
                    }
                } else if (error.status === 405) {
                    if (error.response.data.detail.indexOf("CodeRevoked") > -1) {
                        setActivationError("This code has been revoked and cannot be activated.");
                    } else if (error.response.data.detail === "CodeUsedTooManyTimes") {
                        setActivationError("This code has been used the maximum allowed and cannot be used again.");
                    }
                } else {
                    setActivationError(`Activation failed with status ${error.status}: ${error.response.data.detail}`);
                }
            }
        }
    };

    const revokeLicense = async () => {
        try {
            await server.revokeLicense(props.activeLicense!.code);
            resetState();
        } catch (error) {
            if (error) {
                if (error.status === 400) {
                    if (error.response.data.detail.indexOf("CodeIsNotValid") > -1) {
                        setActivationError("Code is not valid. Check the code and try again.");
                    }
                } else {
                    setActivationError(`Revokation failed with status ${error.status}: ${error.response.data.detail}`);
                }
            }
        }
    };

    const downloadLicenseFiles = async () => {
        if (props.activeLicense) {
            await server.downloadLicenseFiles(props.activeLicense.code);
            resetState();
        }
    };

    const viewLicenseFiles = async () => {
        if (props.activeLicense) {
            const licenseFile = await server.viewLicense(props.activeLicense.code);
            const fileContent = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), licenseFile], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(fileContent);
            const anchorElement = <Button as="a" href={url} download="license.lic" data-text={licenseFile}>Download</Button>;
            props.setLicenseFileElement(anchorElement);
            resetState();
        }
    };


    const transferLicense = async () => {
        if (props.activeLicense) {
            await server.transferLicense(
                props.activeLicense.code,
                hostid
            );
            setTransferState(2);
        }
    };

    return (
        <CardFooter className={styles.grid}>
            <div className={styles.left}>
                {(props.activeLicense && !props.activeLicense.revoked && props.activeLicense.used < props.activeLicense.allowed)
                    ? <Dialog>
                        <DialogTrigger disableButtonEnhancement>
                            <Button>Manually Activate</Button>
                        </DialogTrigger>
                        <DialogSurface>
                            <DialogBody>
                                <DialogContent className={styles.hostIdInput}>
                                    <Input contentBefore="host=" className={styles.hostIdInput} placeholder="Enter host id for activation, example: 'lapthink-30'" onChange={(ev, data: InputOnChangeData) => setHostid(data.value)} />
                                </DialogContent>
                                <DialogActions>
                                    <DialogTrigger disableButtonEnhancement>
                                        <Button appearance="primary" onClick={activateProduct}>Continue</Button>
                                    </DialogTrigger>
                                    <DialogTrigger disableButtonEnhancement>
                                        <Button appearance="secondary">Close</Button>
                                    </DialogTrigger>
                                </DialogActions>
                            </DialogBody>
                        </DialogSurface>
                    </Dialog>
                    : null}
                {props.activeLicense && !props.activeLicense.revoked && props.activeLicense.used > 0
                    ? <Button onClick={viewLicenseFiles}>View License File</Button>
                    : null}
                {props.activeLicense && !props.activeLicense.revoked && props.activeLicense.used > 0
                    ? <Button onClick={downloadLicenseFiles}>Download License File</Button>
                    : null}
            </div>
            <div className={styles.right}>
                {props.activeLicense
                    ? <Menu positioning={{ autoSize: true }}>
                        <MenuTrigger disableButtonEnhancement>
                            <Button icon={<MoreHorizontalRegular />}/>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                {!props.activeLicense.revoked ? <MenuItem onClick={() => setRevokeDialogOpen(true)} style={{ color: 'black' }}>Revoke</MenuItem> : null}
                                {props.activeLicense.used > 0 ? <MenuItem onClick={() => setTransferState(1)} style={{ color: 'black' }}>Transfer</MenuItem> : null}
                                {props.activeLicense.licenseType === licenseMap.NEW ? <MenuItem onClick={() => navigate(`/licenses/renew#${props.activeLicense?.code}`)} style={{ color: 'black' }}>Renew</MenuItem> : null}
                                <MenuItem onClick={() => showHistoryDialog(true)} style={{ color: 'black' }}>View History</MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    : null}
            </div>
            <Dialog open={revokeDialogOpen}>
                <DialogSurface>
                    <DialogBody>
                        <DialogContent className={styles.hostIdInput}>
                            <Body1>Are you sure you want to revoke this license? This action <b>cannot be undone</b> and will break any customers actively using this code.</Body1>
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="primary" onClick={() => { revokeLicense(); setRevokeDialogOpen(false); }}>Continue</Button>
                            <Button appearance="secondary" onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <Dialog open={transferState > 0}>
                <DialogSurface>
                    <DialogBody>
                        <DialogContent className={styles.hostIdInput}>
                            <Input contentBefore="host=" className={styles.hostIdInput} placeholder="Enter a new host id" onChange={(ev, data: InputOnChangeData) => setHostid(data.value)} />
                        </DialogContent>
                        <DialogActions>
                            {transferState === 1 ? <Button appearance="primary" disabled={!hostid} onClick={transferLicense}>Continue</Button> : null}
                            {transferState === 2 ? <Button appearance="primary" onClick={() => { downloadLicenseFiles(); setTransferState(0); }}>Download updated files</Button> : null}
                            <Button appearance="secondary" onClick={() => setTransferState(0)}>Close</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <Dialog open={historyDialogOpen}>
                <DialogSurface>
                    <DialogBody>
                        <DialogContent>
                            <List>
                                {props.activeLicense && props.activeLicense.history.length > 0 ? props.activeLicense.history.map((his) => <ListItem key={his}>{formatHistoryString(his)}</ListItem>) : <ListItem>There have been zero renewals or transfers of this license.</ListItem>}
                            </List>
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={() => showHistoryDialog(false)}>Close</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </CardFooter>
    )
};

const isoDateTimeRE = /(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?/g;
function formatHistoryString(s: string): string {
  return s.replace(isoDateTimeRE, (_, ymd, hms) => {
    const d = new Date(`${ymd}T${hms}`);
    return d.toLocaleString();
  });
}

export { CustomerCardFooder };